import { useATSQuery } from '@viome/viome-base/src/api/Strapi/useStrapi';
import type {
  AtsMetaData,
  Section,
} from '@viome/viome-base/src/api/Strapi/StrapiTypes';

/** Section IDs authored in Strapi addOnDetail / AtsMetaData */
const SECTION_IDS = {
  howToUse: '1',
  howItWorks: '2',
  whatsInside: '3',
  delivered: '4',
  supplement: '5',
  faq: '6',
  disclaimer: '7',
} as const;

function findSectionById(
  sections: Section[],
  sectionId: string,
): Section | undefined {
  return sections.find((s) => s.sectionId === sectionId);
}

function findSectionByTitle(
  sections: Section[],
  title: string,
): Section | undefined {
  return sections.find(
    (s) => s.title.toLowerCase() === title.toLowerCase(),
  );
}

function findSection(
  sections: Section[],
  sectionId: string,
  title: string,
): Section | undefined {
  return (
    findSectionById(sections, sectionId) ??
    findSectionByTitle(sections, title)
  );
}

export function getATSSections(data: Pick<AtsMetaData, 'sections'>) {
  const sections = Array.isArray(data?.sections) ? data.sections : [];
  return {
    howToUse: findSection(sections, SECTION_IDS.howToUse, 'How to use it'),
    howItWorks: findSection(sections, SECTION_IDS.howItWorks, 'How it works'),
    whatsInside: findSection(
      sections,
      SECTION_IDS.whatsInside,
      "What's inside",
    ),
    delivered:
      findSectionById(sections, SECTION_IDS.delivered) ??
      sections.find((s) => s.highlights?.length),
    supplement:
      findSectionById(sections, SECTION_IDS.supplement) ??
      sections.find((s) => s.ingredients?.length),
    faq:
      findSectionById(sections, SECTION_IDS.faq) ??
      sections.find((s) => s.faqs?.length),
    disclaimer:
      findSectionById(sections, SECTION_IDS.disclaimer) ??
      sections.find((s) => s.text),
  };
}

export function useATSData() {
  const { data, isLoading, isError } = useATSQuery();

  return {
    data: data ?? null,
    isLoading,
    isError,
  };
}
